/**
 * LearningPlatformPromo — cross-sell card for the D&B Cloud Learning
 * Platform. Surfaces in three places on the exam app:
 *
 *   variant="wide"   — full-width banner on the cert-selection landing
 *   variant="modal"  — slim card on the post-exam results modal
 *   variant="pill"   — compact pill in the topbar near Sign Out
 *
 * Honest framing: subscriptions on the exam app and the learning platform
 * are SEPARATE Stripe products. If a user upgrades to the learning platform
 * without canceling here first, they'll be billed for both. The card
 * always exposes a "Cancel my exam subscription" link (Stripe billing
 * portal) for users who already have an exam subscription.
 *
 * Suppression: when tierSource === 'platform', the user already has a
 * Learning Platform subscription — pitching the platform to them would
 * be tone-deaf. Render nothing in that case.
 *
 * Props:
 *   variant: 'wide' | 'modal' | 'pill'
 *   hasExamSub: boolean — when true, show the cancellation disclaimer +
 *                         portal link. When false, show only the upsell.
 *   tierSource: 'exam' | 'platform' | 'none' | undefined
 *               When 'platform', the component returns null.
 *   onOpenPortal: () => void — opens a Stripe billing-portal session
 *                              (parent component handles fetch + redirect).
 */
import React from 'react';
import './LearningPlatformPromo.css';

const PLATFORM_URL = 'https://dreamandbuildcloudai.com/?utm_source=exam-app&utm_campaign=cross_sell';
const PLATFORM_PRICING_URL = 'https://dreamandbuildcloudai.com/?utm_source=exam-app&utm_campaign=cross_sell#pricing';

export default function LearningPlatformPromo({
  variant = 'wide',
  hasExamSub = false,
  tierSource,
  onOpenPortal,
}) {
  // Don't pitch the Learning Platform to a user who's already paying for
  // it — would be insulting and confusing. Cross-sell only fires for
  // exam-only subscribers, free users, and users we don't have a tier
  // signal for yet.
  if (tierSource === 'platform') return null;
  if (variant === 'pill') {
    return (
      <a
        href={PLATFORM_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="lpp-pill"
        aria-label="Explore the D&B Cloud Learning Platform — labs, capstones, jobs board, plus quizzes & exams"
      >
        ✨ More than exams? See the Learning Platform →
      </a>
    );
  }

  const showDisclaimer = hasExamSub;

  if (variant === 'modal') {
    return (
      <div className="lpp-modal">
        <div className="lpp-modal-eyebrow">✨ Want more than exams?</div>
        <h4 className="lpp-modal-title">D&amp;B Cloud Learning Platform</h4>
        <p className="lpp-modal-body">
          Practice quizzes &amp; timed exams + reading guides, hands-on AWS labs,
          capstone projects, a daily jobs board, and AI-led practice interviews —
          all in one subscription.
        </p>
        <div className="lpp-modal-actions">
          <a
            href={PLATFORM_PRICING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="lpp-cta"
          >
            See plans →
          </a>
          {showDisclaimer && onOpenPortal && (
            <button
              type="button"
              className="lpp-secondary-btn"
              onClick={onOpenPortal}
            >
              Cancel my $14.99 exam subscription
            </button>
          )}
        </div>
        {showDisclaimer && (
          <p className="lpp-disclaimer">
            ⚠️ Heads up: your $14.99/mo exam subscription will keep charging
            until you cancel it here. Cancel before subscribing on the
            Learning Platform to avoid being billed twice.
          </p>
        )}
      </div>
    );
  }

  // wide (default)
  return (
    <section className="lpp-wide" aria-labelledby="lpp-wide-title">
      <div className="lpp-wide-eyebrow">✨ Want more than exam prep?</div>
      <div className="lpp-wide-row">
        <div className="lpp-wide-copy">
          <h3 id="lpp-wide-title" className="lpp-wide-title">
            Get the full D&amp;B Cloud Learning Platform
          </h3>
          <p className="lpp-wide-body">
            Quizzes &amp; timed exams + reading guides, hands-on AWS labs,
            capstone projects with auto-grading, a daily jobs board, and
            AI-led practice interviews — one subscription, every cert path.
          </p>
          <ul className="lpp-wide-features">
            <li>📚 13 cert tracks (Foundation → Specialty)</li>
            <li>🧪 50+ guided AWS labs with sandbox accounts</li>
            <li>🏆 Auto-graded capstone projects with badges</li>
            <li>💼 Daily jobs board, filtered to entry/mid roles</li>
            <li>🎤 AI-led Blitz Interview practice</li>
          </ul>
        </div>
        <div className="lpp-wide-cta-col">
          <a
            href={PLATFORM_PRICING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="lpp-cta"
          >
            See Learning Platform plans →
          </a>
          {showDisclaimer && onOpenPortal && (
            <button
              type="button"
              className="lpp-secondary-btn"
              onClick={onOpenPortal}
            >
              Cancel my $14.99 exam subscription
            </button>
          )}
        </div>
      </div>
      {showDisclaimer && (
        <p className="lpp-disclaimer">
          ⚠️ Heads up: your $14.99/mo exam subscription will keep charging
          until you cancel it here on Dream&amp;Build Exams. Cancel before
          subscribing on the Learning Platform to avoid being billed twice
          — they're separate Stripe products on separate sites.
        </p>
      )}
    </section>
  );
}
